import axios from 'axios'
import type { PostSummary, PostDetail, CategoryCount, PostsQueryOptions } from '../types'

interface ApiMeta {
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
}

interface ApiResponse<T> {
  data: T
  meta: ApiMeta
  error?: string
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5000'

const client = axios.create({
  baseURL: BASE_URL,
  timeout: 10_000,
  headers: { 'Content-Type': 'application/json' },
})

export interface PostsPage {
  items: PostSummary[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

export const postsApi = {
  /**
   * Fetch a paginated list of posts with optional filters.
   */
  async getAll(options: PostsQueryOptions = {}): Promise<PostsPage> {
    const { page = 1, pageSize = 10, category, tag, search } = options
    const params: Record<string, string | number> = { page, pageSize }
    if (category) params['category'] = category
    if (tag) params['tag'] = tag
    if (search) params['q'] = search

    const { data: response } = await client.get<ApiResponse<PostSummary[]>>('/api/posts', { params })
    return {
      items: response.data ?? [],
      total: response.meta.totalCount,
      page: response.meta.page,
      pageSize: response.meta.pageSize,
      totalPages: response.meta.totalPages,
    }
  },

  /**
   * Fetch a single post by its slug.
   */
  async getBySlug(slug: string): Promise<PostDetail | null> {
    try {
      const { data: response } = await client.get<ApiResponse<PostDetail>>(`/api/posts/${slug}`)
      return response.data
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null
      throw err
    }
  },

  /**
   * Fetch all distinct categories with post counts.
   */
  async getCategories(): Promise<CategoryCount[]> {
    const { data: response } = await client.get<ApiResponse<CategoryCount[]>>('/api/posts/categories')
    return response.data ?? []
  },

  /**
   * Fetch all distinct tags with post counts.
   */
  async getTags(): Promise<{ tag: string; count: number }[]> {
    const { data: response } = await client.get<ApiResponse<{ tag: string; count: number }[]>>('/api/posts/tags')
    return response.data ?? []
  },

  /**
   * Record a page view with geo context resolved client-side.
   * No IP address is sent or stored — geo is derived from a third-party lookup
   * whose response we forward without the IP field.
   * Fire-and-forget; errors are silently swallowed so they never affect the reader.
   */
  recordView(slug: string, geo: {
    countryCode?: string | null
    country?: string | null
    region?: string | null
    city?: string | null
    timezone?: string | null
    language?: string | null
  }): void {
    client.post(`/api/posts/${slug}/view`, geo).catch(() => { /* best-effort */ })
  },
}
